import type { BouquetBuildSummary } from "../api/types";

/**
 * G-132 — the rules behind the Ready bouquets view, so the component renders
 * and this decides.
 */

export function bouquetDisplayName(b: BouquetBuildSummary): string {
  return b.name?.trim() || b.template?.name || "Custom bouquet";
}

/** A bouquet is HELD when an order has claimed it: listed, greyed, never counted as ready. */
export function isHeld(b: BouquetBuildSummary): boolean {
  return b.forOrder != null;
}

/** What the chip counts: ready bouquets, i.e. not held for an order. */
export function readyBouquetCount(bouquets: BouquetBuildSummary[]): number {
  return bouquets.filter((b) => !isHeld(b)).length;
}

/** Search matches the bouquet's name or any of its line items' names. */
export function matchesBouquetSearch(b: BouquetBuildSummary, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  if (bouquetDisplayName(b).toLowerCase().includes(q)) return true;
  return b.lines.some((l) => l.item.name.toLowerCase().includes(q));
}

/** Ready first (the server's newest-first order kept), held last. */
export function sortReadyBouquets(bouquets: BouquetBuildSummary[]): BouquetBuildSummary[] {
  return [...bouquets].sort((a, b) => Number(isHeld(a)) - Number(isHeld(b)));
}

/** "Freesia ×7, Rose ×3" and how many more did not fit. */
export function bouquetLinesSummary(
  b: BouquetBuildSummary,
  max = 3,
): { text: string; more: number } {
  const shown = b.lines.slice(0, max).map((l) => `${l.item.name} ×${Number(l.quantity)}`);
  return { text: shown.join(", "), more: Math.max(0, b.lines.length - max) };
}

export interface BouquetShopGroup {
  shopId: string;
  shopName: string;
  /** Ready (not held) bouquets in the group. */
  readyCount: number;
  bouquets: BouquetBuildSummary[];
}

/**
 * One group per shop, in the shops' own order, including shops with nothing —
 * "Sant Martí · none ready" says the shop was looked at. A bouquet at a shop
 * not in `shops` (an inactive one) still gets its group, after the rest.
 */
export function groupBouquetsByShop(
  bouquets: BouquetBuildSummary[],
  shops: { id: string; name: string }[],
): BouquetShopGroup[] {
  const byShop = new Map<string, BouquetBuildSummary[]>();
  for (const b of bouquets) {
    const list = byShop.get(b.location.id);
    if (list) list.push(b);
    else byShop.set(b.location.id, [b]);
  }
  const groups: BouquetShopGroup[] = [];
  const seen = new Set<string>();
  for (const shop of shops) {
    seen.add(shop.id);
    const list = sortReadyBouquets(byShop.get(shop.id) ?? []);
    groups.push({ shopId: shop.id, shopName: shop.name, readyCount: readyBouquetCount(list), bouquets: list });
  }
  for (const [shopId, list] of byShop) {
    if (seen.has(shopId)) continue;
    const sorted = sortReadyBouquets(list);
    groups.push({ shopId, shopName: list[0]!.location.name, readyCount: readyBouquetCount(sorted), bouquets: sorted });
  }
  return groups;
}
